import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {Alert, Box, Button, Group, Select, Stack, Text, Title} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';

import {Account} from '../types';
import {useAccount} from '../hooks/useAccount';
import {useAccounts} from '../hooks/useAccounts';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {rbacActions} from '../../../config/rbac/actions';
import {notifyError, notifySuccess} from '../../../components/PlatformNotification';
import {PlatformDrawer} from '@flexinet/ui-components';

// FNP-575: the account's Billing tab (moved off the left-hand Profile into its own
// tab next to Applications / Users). Shows the current Customer linkage and — for
// admins — lets an operator re-assign which Customer this account owns. The write
// goes through setAccountCustomerId (the single writer); ownerAccountId is the
// FNP-121 deletion-cascade key, so the re-assign is behind a deletion-terms drawer
// (drawer is the platform standard, not a modal).
export const BillingTab = ({record}: { record: Account }) => {

    const {id: accountId} = useParams();
    const {createCustomer, reassignCustomer, fetchRecord} = useAccount(accountId);
    const {allowedActions} = useVerifiedPermissions();
    const {items: accounts} = useAccounts();

    const [opened, {open, close}] = useDisclosure(false);
    const [targetAccountId, setTargetAccountId] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const canReassign = allowedActions.includes(rbacActions.ReassignCustomerOwner);

    // Accounts to pick from (exclude this one — can't reassign to itself).
    const options = accounts
        .filter((a: Account) => a.id !== record.id)
        .map((a: Account) => ({value: a.id, label: a.email}));

    const targetEmail = accounts.find((a: Account) => a.id === targetAccountId)?.email ?? targetAccountId;

    const handleOpen = () => {
        setTargetAccountId(null);
        open();
    };

    const doReassign = async () => {
        if (!targetAccountId || !record.customerId) {
            return;
        }
        setBusy(true);
        try {
            // Re-point the TARGET account at this account's Customer. setAccountCustomerId
            // is an idempotent SET — it overwrites a stale/dangling customerId on the
            // target and emits the linkage event that updates the billing projection.
            await reassignCustomer(targetAccountId, record.customerId);
            notifySuccess('Customer owner re-assigned');
            close();
            await fetchRecord();
        } catch (e) {
            notifyError(e);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack gap="lg">
            <Box>
                <Title order={5}>Customer linkage</Title>
                <Text size="sm" c="dimmed">Customer linkage for billing and invoicing.</Text>
                <Group mt="sm" gap="xs">
                    <Text size="sm" fw={500}>Customer ID:</Text>
                    {record.customerId
                        ? <Text size="sm">{record.customerId}</Text>
                        : <Button size="xs" onClick={() => createCustomer()}>Create Customer</Button>}
                </Group>
            </Box>

            {canReassign && record.customerId && (
                <Box>
                    <Title order={5}>Re-assign owner</Title>
                    <Text size="sm" c="dimmed">
                        Move this account&apos;s Customer to a different owning account.
                    </Text>
                    <Button mt="sm" color="red" variant="light" onClick={handleOpen}>
                        Re-assign owner…
                    </Button>
                </Box>
            )}

            <PlatformDrawer
                opened={opened}
                onClose={close}
                title="Re-assign customer owner"
            >
                <Stack gap="md">
                    <Select
                        label="New owning account"
                        placeholder="Select an account"
                        searchable
                        data={options}
                        value={targetAccountId}
                        onChange={setTargetAccountId}
                    />

                    {targetAccountId && (
                        <Alert color="red" variant="light" title="This is a deletion-affecting change">
                            <Text size="sm">
                                This account&apos;s Customer will be re-assigned to{' '}
                                <strong>{targetEmail}</strong>. From now on that account&apos;s
                                lifecycle — <strong>including deletion</strong> — governs this Customer;
                                this account&apos;s deletion will no longer affect it. This is an
                                account-administration change.
                            </Text>
                        </Alert>
                    )}

                    <Group justify="flex-end">
                        <Button variant="default" onClick={close} disabled={busy}>Cancel</Button>
                        <Button color="red" disabled={!targetAccountId || busy} loading={busy} onClick={doReassign}>
                            Re-assign owner
                        </Button>
                    </Group>
                </Stack>
            </PlatformDrawer>
        </Stack>
    );
};
